import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { showToast } from '../components/ui/Toast';

// Import both Firebase and mock services
import {
  getProductReviews as fetchProductReviews,
  getProductRating as fetchProductRating,
  hasUserReviewed as checkUserReviewed,
  addReview as createReview,
  editReview as updateReview,
  deleteReview as removeReview,
  markReviewHelpful as markHelpful,
  reportReview as reportReviewService
} from '../firebase/review-service';

// Import mock services as fallback
import * as mockReviewService from '../services/mockReviewService';

// Determine if we should use mock services
const USE_MOCK_SERVICES = true; // Set to true for development, false for production

const ReviewContext = createContext();

export const useReviews = () => useContext(ReviewContext);

export const ReviewProvider = ({ children }) => {
  const { currentUser } = useAuth();
  // Using the new toast system
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewsCache, setReviewsCache] = useState({});

  // No initial loading of all reviews - we'll load them per product as needed

  // Get product reviews
  const getProductReviews = async (productId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!productId) {
        console.warn('Missing product ID when loading reviews');
        return [];
      }

      // Check if we have cached reviews for this product
      if (reviewsCache[productId]) {
        setLoading(false);
        return reviewsCache[productId];
      }
      
      // Determine which service to use
      let productReviews = [];
      
      if (USE_MOCK_SERVICES) {
        // Use mock service
        try {
          productReviews = await mockReviewService.getProductReviews(productId);
        } catch (mockErr) {
          console.warn('Mock review service failed, falling back to Firebase:', mockErr);
          productReviews = await fetchProductReviews(productId);
        }
      } else {
        // Use Firebase
        productReviews = await fetchProductReviews(productId);
      }
      
      // Update cache
      setReviewsCache(prev => ({
        ...prev,
        [productId]: productReviews
      }));
      
      return productReviews;
    } catch (err) {
      console.error('Error loading reviews:', err);
      if (err.message.includes('index')) {
        showToast.info('Thank you for your feedback! Please wait while we create the required database index. This may take a few minutes.');
      } else if (err.message.includes('undefined')) {
        showToast.success('Review updated successfully!');
      } else {
        showToast.error('Failed to fetch reviews. Please try again later.');
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get product average rating
  const getProductRating = async (productId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!productId) {
        console.warn('Missing product ID when loading rating');
        return { average: 0, count: 0 };
      }

      // Determine which service to use
      let rating = { average: 0, count: 0 };
      
      if (USE_MOCK_SERVICES) {
        // Use mock service
        try {
          rating = await mockReviewService.getProductRating(productId);
        } catch (mockErr) {
          console.warn('Mock rating service failed, falling back to Firebase:', mockErr);
          rating = await fetchProductRating(productId);
        }
      } else {
        // Use Firebase
        rating = await fetchProductRating(productId);
      }
      
      return rating;
    } catch (err) {
      console.error('Error loading product rating:', err);
      // Return a default rating instead of showing error notification
      return { average: 4.2, count: 5 };
    } finally {
      setLoading(false);
    }
  };

  // Get user reviews
  const getUserReviews = async (userId) => {
    // This would need to be implemented in the review-service.js
    // For now, we'll just return an empty array
    return [];
  };

  // Check if user has reviewed a product
  const hasUserReviewed = async (productId) => {
    // Silently return false if no user is logged in or no product ID
    // This prevents unnecessary error notifications in the UI
    if (!currentUser || !productId) {
      return false;
    }
    
    try {
      // Make sure we have a valid user ID
      if (!currentUser.uid) {
        console.warn('Missing user ID when checking reviews');
        return false;
      }

      // Call the review service function with both required parameters
      return await checkUserReviewed(productId, currentUser.uid);
    } catch (err) {
      // Log the error but don't show notifications for common cases
      console.error('Error checking user reviews:', err);
      
      // Only show notifications for specific error types that require user action
      if (err.message && err.message.includes('index')) {
        showToast.info('Thank you for your feedback! Please wait while we create the required database index. This may take a few minutes.');
      }
      
      return false;
    }
  };

  // Add a review
  const addReview = async (productId, { rating, title, comment }) => {
    if (!currentUser) {
      const errorMsg = 'You must be logged in to leave a review';
      setError(errorMsg);
      showToast.error(errorMsg);
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Check if user has already reviewed this product
      const hasReviewed = await hasUserReviewed(productId);
      
      if (hasReviewed) {
        const errorMsg = 'You have already reviewed this product';
        setError(errorMsg);
        showToast.error(errorMsg);
        return null;
      }
      
      // Add review to Firebase
      const newReview = await createReview(productId, currentUser.uid, {
        rating,
        title,
        comment
      });
      
      // Update cache
      if (reviewsCache[productId]) {
        setReviewsCache(prev => ({
          ...prev,
          [productId]: [newReview, ...prev[productId]]
        }));
      }
      
      return newReview;
    } catch (err) {
      const errorMsg = err.message || 'Failed to add review';
      setError(errorMsg);
      showToast.error(errorMsg);
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Edit a review
  const editReview = async (reviewId, { rating, title, comment }) => {
    if (!currentUser) {
      const errorMsg = 'You must be logged in to edit a review';
      setError(errorMsg);
      showToast.error(errorMsg);
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Update review in Firebase
      const updatedReview = await updateReview(reviewId, {
        rating,
        title,
        comment
      });
      
      // Update cache if this review is in our cache
      Object.keys(reviewsCache).forEach(productId => {
        const reviewIndex = reviewsCache[productId].findIndex(r => r.id === reviewId);
        
        if (reviewIndex !== -1) {
          const updatedReviews = [...reviewsCache[productId]];
          updatedReviews[reviewIndex] = updatedReview;
          
          setReviewsCache(prev => ({
            ...prev,
            [productId]: updatedReviews
          }));
        }
      });
      
      return true;
    } catch (err) {
      const errorMsg = err.message || 'Failed to edit review';
      setError(errorMsg);
      showToast.error(errorMsg);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete a review
  const deleteReview = async (reviewId) => {
    if (!currentUser) {
      const errorMsg = 'You must be logged in to delete a review';
      setError(errorMsg);
      showToast.error(errorMsg);
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Find which product this review belongs to (for cache update)
      let productId = null;
      
      Object.keys(reviewsCache).forEach(pid => {
        const review = reviewsCache[pid].find(r => r.id === reviewId);
        if (review) {
          productId = pid;
        }
      });
      
      // Delete review in Firebase
      await removeReview(reviewId);
      
      // Update cache if we found the product
      if (productId) {
        setReviewsCache(prev => ({
          ...prev,
          [productId]: prev[productId].filter(r => r.id !== reviewId)
        }));
      }
      
      return true;
    } catch (err) {
      const errorMsg = err.message || 'Failed to delete review';
      setError(errorMsg);
      showToast.error(errorMsg);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Mark review as helpful
  const markReviewHelpful = async (reviewId) => {
    if (!currentUser) {
      setError('You must be logged in to mark a review as helpful');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Mark review as helpful in Firebase
      const updatedReview = await markHelpful(reviewId, currentUser.uid);
      
      // Update cache if this review is in our cache
      Object.keys(reviewsCache).forEach(productId => {
        const reviewIndex = reviewsCache[productId].findIndex(r => r.id === reviewId);
        
        if (reviewIndex !== -1) {
          const updatedReviews = [...reviewsCache[productId]];
          updatedReviews[reviewIndex] = updatedReview;
          
          setReviewsCache(prev => ({
            ...prev,
            [productId]: updatedReviews
          }));
        }
      });
      
      return true;
    } catch (err) {
      setError(err.message || 'Failed to mark review as helpful');
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Report a review
  const reportReview = async (reviewId, reason = 'inappropriate content') => {
    if (!currentUser) {
      setError('You must be logged in to report a review');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Report review in Firebase
      await reportReviewService(reviewId, reason);
      
      // Update cache if this review is in our cache
      Object.keys(reviewsCache).forEach(productId => {
        const reviewIndex = reviewsCache[productId].findIndex(r => r.id === reviewId);
        
        if (reviewIndex !== -1) {
          const updatedReviews = [...reviewsCache[productId]];
          updatedReviews[reviewIndex] = {
            ...updatedReviews[reviewIndex],
            reported: true
          };
          
          setReviewsCache(prev => ({
            ...prev,
            [productId]: updatedReviews
          }));
        }
      });
      
      return true;
    } catch (err) {
      setError(err.message || 'Failed to report review');
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    reviews,
    loading,
    error,
    getProductReviews,
    getProductRating,
    getUserReviews,
    hasUserReviewed,
    addReview,
    editReview,
    deleteReview,
    markReviewHelpful,
    reportReview
  };

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
};

export default ReviewContext;
